
import React, { useEffect, useRef } from "react";

import "./EvaBuildingViewer.css";


//========================================================
// EVA Building Viewer
//
// Giraffe360 Automatic Tour Experiment
//
// Uses Giraffe shared-view URLs (?v=...)
//
// Tests:
// 1. Programmatic panorama changes via iframe URL
// 2. Viewer reload duration
// 3. Visual continuity between panoramas
// 4. Automatic timed navigation
//
// No changes to existing CSS or layout.
//========================================================


//========================================================
// Configuration
//========================================================

const TOUR_URL =
    "https://tour.giraffe360.com/49c74ed43a75417c96cf6a2684fac357";

const TOUR_ORIGIN =
    "https://tour.giraffe360.com";

const TOUR_INTERVAL_MS = 8000;


//========================================================
// Component
//========================================================

export default function EvaBuildingViewer() {

    //----------------------------------------------------
    // References
    //----------------------------------------------------

    const giraffeIframeRef = useRef(null);

    const tourUrlsRef = useRef([]);

    const currentIndexRef = useRef(-1);

    const tourTimerRef = useRef(null);

    const runningRef = useRef(false);

    const loadingRef = useRef(false);

    const loadStartedRef = useRef(null);

    const currentSceneRef = useRef(null);

    const expectedUrlRef = useRef(TOUR_URL);

    const mountedRef = useRef(false);


    //----------------------------------------------------
    // Validate Shared-View URL
    //----------------------------------------------------


	const validateTourUrl = (url) => {

	    if (
	        typeof url !== "string" ||
	        !url.trim()
	    ) {
	        return false;
	    }

	    try {

	        const parsed = new URL(url.trim());
	        const base = new URL(TOUR_URL);

	        return (
	            parsed.origin === base.origin &&
	            parsed.pathname.replace(/\/$/, "") ===
	                base.pathname.replace(/\/$/, "")
	        );

	    } catch (error) {

	        return false;

	    }

	};



    //----------------------------------------------------
    // Stop Timer
    //----------------------------------------------------

    const clearTourTimer = () => {

        if (tourTimerRef.current) {

            clearTimeout(
                tourTimerRef.current
            );

            tourTimerRef.current = null;

        }

    };


    //----------------------------------------------------
    // Stop Automatic Tour
    //----------------------------------------------------

    const stopTour = () => {

        runningRef.current = false;

        clearTourTimer();

        console.log(
            "⏹️ GIRAFFE AUTO TOUR STOPPED"
        );

    };


    //----------------------------------------------------
    // Load Tour Location
    //----------------------------------------------------

    const loadLocation = (index) => {

        const urls =
            tourUrlsRef.current;

        if (
            index < 0 ||
            index >= urls.length
        ) {

            console.warn(
                "🦒 Invalid tour index:",
                index
            );

            return false;

        }

        const url =
            urls[index];

        const iframe =
            giraffeIframeRef.current;

        if (!iframe) {

            console.warn(
                "🦒 Giraffe iframe unavailable"
            );

            return false;

        }

        //------------------------------------------------
        // Record Navigation
        //----------------------------------------------------

        currentIndexRef.current = index;

        expectedUrlRef.current = url;

        currentSceneRef.current = null;

        loadingRef.current = true;

        loadStartedRef.current =
            performance.now();

        console.log(
            "%c🚶 AUTO NAVIGATING",
            "color:#0088cc;font-weight:bold;"
        );

        console.log(
            "Location:",
            index + 1,
            "of",
            urls.length
        );

        console.log(
            "URL:",
            url
        );

        //------------------------------------------------
        // Change Iframe Location
        //----------------------------------------------------

        iframe.src = url;

        return true;

    };


    //----------------------------------------------------
    // Schedule Next Location
    //----------------------------------------------------

    const scheduleNextLocation = () => {

        clearTourTimer();

        if (!runningRef.current) {
            return;
        }

        if (tourUrlsRef.current.length < 2) {
            return;
        }

        tourTimerRef.current =
            setTimeout(() => {

                if (!runningRef.current) {
                    return;
                }

                const nextIndex =
                    (
                        currentIndexRef.current + 1
                    ) % tourUrlsRef.current.length;

                loadLocation(nextIndex);

            }, TOUR_INTERVAL_MS);

    };


    //----------------------------------------------------
    // Navigate To Next Location
    //----------------------------------------------------

    const nextLocation = () => {

        const urls =
            tourUrlsRef.current;

        if (urls.length < 2) {

            console.warn(
                "🦒 Configure at least two URLs first"
            );

            return;
        }

        clearTourTimer();

        const nextIndex =
            (
                currentIndexRef.current + 1
            ) % urls.length;

        const loaded =
            loadLocation(nextIndex);

        if (!loaded && runningRef.current) {

            scheduleNextLocation();

        }

    };


    //----------------------------------------------------
    // Start Automatic Tour
    //----------------------------------------------------

    const startTour = () => {

        if (tourUrlsRef.current.length < 2) {

            console.warn(
                "🦒 Please configure two shared-view URLs"
            );

            return;
        }

        if (runningRef.current) {

            console.log(
                "🦒 Automatic tour already running"
            );

            return;
        }

        runningRef.current = true;

        console.log(
            "%c▶️ GIRAFFE AUTO TOUR STARTED",
            "color:#00aa88;font-weight:bold;font-size:14px;"
        );

        //------------------------------------------------
        // Start With First Location
        //----------------------------------------------------

        loadLocation(0);

    };


    //----------------------------------------------------
    // Configure Tour URLs
    //----------------------------------------------------

    const setTourUrls = (urls) => {

        if (
            !Array.isArray(urls) ||
            urls.length < 2
        ) {

            console.warn(
                "🦒 Supply an array of at least two URLs"
            );

            return false;

        }

        const invalid =
            urls.filter(
                url => !validateTourUrl(url)
            );

        if (invalid.length > 0) {

            console.warn(
                "🦒 Invalid Giraffe shared-view URLs:",
                invalid
            );

            return false;

        }

        stopTour();

        tourUrlsRef.current =
            urls.map(url => url.trim());

        currentIndexRef.current = -1;

        console.log(
            "%c🦒 TOUR LOCATIONS CONFIGURED",
            "color:#00aa88;font-weight:bold;"
        );

        console.table(
            tourUrlsRef.current.map(
                (url, index) => ({

                    location: index + 1,

                    url

                })
            )
        );

        return true;

    };


    //----------------------------------------------------
    // Handle Scene Change
    //----------------------------------------------------

    const handleSceneChange = (sceneKey) => {

        if (!sceneKey) {
            return;
        }

        if (
            currentSceneRef.current !== sceneKey
        ) {

            currentSceneRef.current = sceneKey;

            console.log(
                "📍 GIRAFFE SCENE:",
                sceneKey
            );

        }

    };


    //----------------------------------------------------
    // Handle Iframe Load
    //----------------------------------------------------

    const handleIframeLoad = () => {

        //------------------------------------------------
        // Ignore Initial Load Timing
        //----------------------------------------------------

        if (
            loadStartedRef.current === null
        ) {

            console.log(
                "🦒 Giraffe initial iframe loaded"
            );

            return;

        }

        //------------------------------------------------
        // Measure Navigation Load
        //----------------------------------------------------

        const elapsed =
            performance.now() -
            loadStartedRef.current;

        loadStartedRef.current = null;

        loadingRef.current = false;

        console.log(
            "%c🟢 GIRAFFE IFRAME LOADED",
            "color:#00aa88;font-weight:bold;"
        );

        console.log(
            "Location:",
            currentIndexRef.current + 1
        );

        console.log(
            "Load time:",
            Math.round(elapsed),
            "ms"
        );

        console.log(
            "Expected URL:",
            expectedUrlRef.current
        );

        //------------------------------------------------
        // Begin Dwell Period After Load
        //----------------------------------------------------

        scheduleNextLocation();

    };


    //----------------------------------------------------
    // Setup Message Listener
    //----------------------------------------------------

    useEffect(() => {

        mountedRef.current = true;

        const handleMessage = (event) => {

            const iframe =
                giraffeIframeRef.current;

            if (
                !iframe ||
                event.source !== iframe.contentWindow ||
                event.origin !== TOUR_ORIGIN
            ) {

                return;

            }

            const data =
                event.data;

            if (
                !data ||
                typeof data !== "object"
            ) {

                return;

            }

            //------------------------------------------------
            // Scene Change
            //----------------------------------------------------

            if (
                data.type ===
                    "g360:scene-change"
            ) {

                handleSceneChange(
                    data.sceneKey
                );

            }

        };

        window.addEventListener(
            "message",
            handleMessage
        );

        //------------------------------------------------
        // Console Controls
        //----------------------------------------------------

        window.omniscanGiraffe = {

            setUrls:
                setTourUrls,

            start:
                startTour,

            stop:
                stopTour,

            next:
                nextLocation,

            currentScene: () =>
                currentSceneRef.current,

            status: () => ({

                running:
                    runningRef.current,

                loading:
                    loadingRef.current,

                location:
                    currentIndexRef.current + 1,

                totalLocations:
                    tourUrlsRef.current.length,

                sceneKey:
                    currentSceneRef.current

            })

        };

        console.log(
            "%c🦒 GIRAFFE AUTO TOUR READY",
            "color:#00aa88;font-weight:bold;font-size:14px;"
        );

        console.log(
            "Configure: window.omniscanGiraffe.setUrls([...])"
        );

        console.log(
            "Start: window.omniscanGiraffe.start()"
        );

        console.log(
            "Stop: window.omniscanGiraffe.stop()"
        );

        console.log(
            "Next: window.omniscanGiraffe.next()"
        );

        //------------------------------------------------
        // Cleanup
        //----------------------------------------------------

        return () => {

            mountedRef.current = false;

            stopTour();

            window.removeEventListener(
                "message",
                handleMessage
            );

            delete window.omniscanGiraffe;

        };

    }, []);


    //----------------------------------------------------
    // Render
    //----------------------------------------------------

    return (

        <iframe
            ref={giraffeIframeRef}
            className="eva-demo-viewer-frame"
            src={TOUR_URL}
            title="Eva Estate Experience"
            allow="fullscreen"
            allowFullScreen
            onLoad={handleIframeLoad}
        />

    );

}
